import * as OfferingActions from '../../actions/offering-actions';
import * as ReasonsActions from '../../../ng2/main/reasons/reasons.actions.ts';

module.exports = {
  templateUrl: 'main/components/upcoming-appointments/upcoming-appointments.html',
  controller: class UpcomingAppointmentsController {

    static $inject = [
      '$filter',
      '$scope',
      '$ngRedux',
      '$state',
      '$translate',
      'Appointments',
      'LoadingService',
      'Offerings',
      'PatientAppContextService',
      'AppointmentReasons',
      'ReasonService',
      'ProductService',
    ];

    constructor(
      $filter,
      $scope,
      $ngRedux,
      $state,
      $translate,
      Appointments,
      LoadingService,
      Offerings,
      PatientAppContextService,
      AppointmentReasons,
      ReasonService,
      ProductService,
    ) {
      this.$filter = $filter;
      this.$ngRedux = $ngRedux;
      this.$scope = $scope;
      this.$state = $state;
      this.$translate = $translate;
      this.Appointments = Appointments;
      this.LoadingService = LoadingService;
      this.Offerings = Offerings;
      this.PatientAppContextService = PatientAppContextService;
      this.AppointmentReasons = AppointmentReasons;
      this.ReasonService = ReasonService;
      this.ProductService = ProductService;
    }

    $onInit() {
      this.context = this.PatientAppContextService.context;
      this.patient = this.context.patient;
      this.groupId = this.context.group.id;
      this.groupName = this.context.group.name;
      this.groupPhone = this.context.group.mainContactNumber;
      this.familyDoctorLabel = this.context.group.familyDoctorLabel;
      this.hasFamilyDoctor = !!this.patient.subscription.account;
      this.isNewPatient = this.patient.subscription.isNewPatient;
      this.profileCompleted = this.patient.subscription.profileCompleted;
      this.hasPreventBooking = this.patient.subscription.preventBooking;
      this.mobileCharacterLimit = 85;
      this.showModal = false;

      this.$scope.$on('$destroy', this.$ngRedux.connect(state => ({
        isHospitalBooking: state.hospitalBooking.isHospitalBooking,
      }))(this));

      this.manualConfirmationDone = false;

      this.reasons = [];

      this.LoadingService.waitForAll([
        this.Appointments.query({ groupId: this.groupId, patientId: this.patient.id, filter: 'upcoming' }, (appointments) => {
          this.list = _.sortBy(appointments, appointment => moment(appointment.availability.startTime).unix());

          this.showVisitReason = _.some(this.list, appointment => !!appointment.visitReason);
        }).$promise,
        this.Offerings.query({ group_id: this.groupId, patient_id: this.patient.id }, (offerings) => {
          this.offerings = offerings.sort((a, b) => a.displaySequence - b.displaySequence);

          const reasonsPromises =
            this.offerings.filter(offering => offering.code !== 'WALK_IN')
            .map(offering => this.AppointmentReasons.query(
              {
                group_id: this.groupId,
                patient_id: this.patient.id,
                offering_id: offering.id,
              },
              (reasons) => {
                this.reasons.push(...reasons);
                this.$ngRedux.dispatch(new ReasonsActions.AddReasons(reasons));
              },
            ).$promise);
          this.LoadingService.waitForAll(reasonsPromises);
        }).$promise,
      ]);
    }

    offeringWarningMessages() {
      const messages = [];

      if (!this.hasManitobaProduct) {
        _.each(this.offerings, (offering) => {
          const errorMessage = this.getOfferingErrorMessage(offering);
          if (errorMessage !== null) {
            messages.push(errorMessage);
          }
        },
        );
      }

      if (!this.offerings || this.offerings.length === 0) {
        messages.push(this.$translate.instant('home.warnings.no_offering'));
      }

      return messages;
    }

    isOfferDisabled(offer) {
      return !this.list || this.hasPreventBooking ||
        (!offer.allowMultipleAppointments && this.hasExistingAppointmentOfType(offer.code)) ||
        (!this.hasFamilyDoctor && offer.familyDoctorMandatory) ||
        this.isOfferingWithSingleDeactivatedReason(offer);
    }

    isOfferingWithSingleDeactivatedReason(offering) {
      const reasons = this.reasons.filter(reason => reason.offering.id === offering.id);
      return reasons.length === 1 && this.ReasonService.isReasonUnaccessible(reasons[0]);
    }

    canCancelAppointment(appointment) {
      return !appointment.cancellationLimitTime || moment(appointment.cancellationLimitTime).isBefore(appointment.availability.startTime);
    }

    virtualMeetingAvailable(appointment) {
      return this.ProductService.isAvailable('BOOKING_VIRTUAL_APPOINTMENTS') && appointment.virtualMeeting !== null && appointment.virtualMeeting !== undefined && appointment.virtualMeeting.active;
    }

    linkWillAppear(appointment) {
      if (!this.virtualMeetingAvailable(appointment)) {
        return false;
      }
      return !this.virtualMeetingFailed(appointment) && moment() <= moment(appointment.availability.startTime).subtract(10, 'minutes');
    }

    virtualMeetingFailed(appointment) {
      if (this.virtualMeetingAvailable(appointment)) {
        return appointment.virtualMeeting.creationRetryCount >= 5;
      }
      return false;
    }

    canDisplayMeetingLinks(appointment) {
      if (!this.virtualMeetingAvailable(appointment)) {
        return false;
      }
      return !this.linkWillAppear(appointment) && !!(appointment.virtualMeeting && appointment.virtualMeeting.joinUrl);
    }

    copyLink(appointment) {
      navigator.clipboard.writeText(appointment.virtualMeeting.joinUrl).then(() => { }, () => { });
    }

    join(appointment) {
      window.open(appointment.virtualMeeting.joinUrl, '_blank');
    }

    isPresenceConfirmed(appointment) {
      return appointment.manualConfirmationDone || (appointment.appointmentPatientStatuses && !!_.find(appointment.appointmentPatientStatuses, status => !status.cancelledAt && status.patientStatus.kind === 'confirmation'));
    }

    hasExistingAppointmentOfType(code) {
      return _.some(this.list, { availability: { reason: { offering: { code } } } });
    }

    getOfferingErrorMessage(offering) {
      if (!offering.allowMultipleAppointments && this.hasExistingAppointmentOfType(offering.code)) {
        if (offering.familyDoctorMandatory) {
          return this.$translate.instant('home.warnings.family_physician', { familyDoctorLabel: this.familyDoctorLabel });
        } else if (offering.code === this.PatientAppContextService.walkinCode) {
          return this.$translate.instant('home.warnings.walkin');
        }
        return this.$translate.instant('home.warnings.other_offering', { description: offering.description.toLowerCase() });
      } else if (!this.hasFamilyDoctor && offering.familyDoctorMandatory) {
        return this.$translate.instant('home.warnings.no_family_doctor', {
          description: offering.description.toLowerCase(),
          familyDoctorLabel: this.familyDoctorLabel,
        });
      }
      return null;
    }

    hasWarnings() {
      return !this.isHospitalBooking && ((this.offeringWarningMessages().length > 0) ||
        (this.isNewPatient && !this.profileCompleted) ||
        this.hasPreventBooking);
    }

    displayReasonsForOffering(id) {
      this.$ngRedux.dispatch(OfferingActions.resetOffering());

      this.$state.params.offeringId = id;
      this.$state.go('patients.id.reasons', this.$state.params);
    }

    contextualNoticeShownInMobile() {
      return angular.element('.contextual-notice > .contextual-message').length > angular.element('.contextual-notice > .-not-shown-in-mobile').length;
    }

    clinicPatientMessage() {
      if (this.hasFamilyDoctor) {
        return this.$filter('linky')(this.context.group.patientMessageWithFamilyDoctor, '_blank');
      }
      return this.$filter('linky')(this.context.group.patientMessageWithoutFamilyDoctor, '_blank');
    }

    openModal(appointment, action) {
      this.showModal = true;
      appointment.action = action;
      appointment.confirmMessage = this.$translate.instant(`hospital_booking.confirm_action.${action}`);
    }

    closeModal() {
      this.showModal = false;
    }

    confirmAction(appointment, action) {
      appointment.showMessage = true;
      appointment.message = `${this.$translate.instant(`hospital_booking.action_feedback.${action}`)} (${moment().format('LL - HH:mm')})`;
      this.closeModal();
    }

    confirmAppointment(appointment) {
      appointment.waitingForConfirmationResponse = true;
      appointment.$confirm(() => {
        appointment.manualConfirmationDone = true;
        appointment.waitingForConfirmationResponse = false;
      }, () => {
        appointment.waitingForConfirmationResponse = false;
      });
    }

    get hasManitobaProduct() {
      return this.ProductService.isAvailable('MANITOBA_BOOKING');
    }
  },
};



// WEBPACK FOOTER //
// ./main/components/upcoming-appointments/upcoming-appointments.js